import React, { useState, useEffect } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  FormControl,
  Select,
  MenuItem,
  Typography,
  Link,
  CircularProgress,
  Tooltip
} from '@mui/material';
import { collection, query, onSnapshot, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { format } from 'date-fns';
import { logger } from '../../utils/logger';

const SuggestionsTable = () => {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // 'all', 'pending', 'complete'

  useEffect(() => {
    const q = query(collection(db, 'venue_suggestions'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const suggestionsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        created_at: doc.data().created_at?.toDate()
      }));
      setSuggestions(suggestionsData);
      setLoading(false);
    }, (error) => {
      logger.error('Error fetching suggestions:', error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const handleStatusChange = async (suggestionId, newStatus) => {
    try {
      await updateDoc(doc(db, 'venue_suggestions', suggestionId), {
        status: newStatus
      });
      logger.log(`Updated suggestion ${suggestionId} status to ${newStatus}`);
    } catch (error) {
      logger.error('Error updating suggestion status:', error);
    }
  };

  const handleDelete = async (suggestionId, venueName) => {
    if (!window.confirm(`Delete suggestion for "${venueName}"? This cannot be undone.`)) {
      return;
    }
    try {
      await deleteDoc(doc(db, 'venue_suggestions', suggestionId));
      logger.info('Deleted venue suggestion:', suggestionId);
    } catch (error) {
      logger.error('Error deleting venue suggestion:', error);
    }
  };

  const filteredSuggestions = suggestions.filter(suggestion => {
    if (filter === 'all') return true;
    return suggestion.status === filter;
  });

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <FormControl size="small">
          <Select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          >
            <MenuItem value="all">All Suggestions</MenuItem>
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="complete">Complete</MenuItem>
          </Select>
        </FormControl>
        <Typography variant="body2" color="text.secondary">
          Showing {filteredSuggestions.length} suggestions
        </Typography>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Venue</TableCell>
                <TableCell>Suggestion</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredSuggestions.map((suggestion) => (
                <TableRow key={suggestion.id}>
                  <TableCell>
                    {suggestion.created_at ? 
                      format(suggestion.created_at, 'MMM d, yyyy HH:mm') : 
                      'N/A'
                    }
                  </TableCell>
                  <TableCell>{suggestion.venue_name}</TableCell>
                  <TableCell sx={{ maxWidth: '300px', whiteSpace: 'pre-wrap' }}>
                    {suggestion.suggestion}
                  </TableCell>
                  <TableCell>
                    <Select
                      size="small"
                      value={suggestion.status}
                      onChange={(e) => handleStatusChange(suggestion.id, e.target.value)}
                    >
                      <MenuItem value="pending">Pending</MenuItem>
                      <MenuItem value="complete">Complete</MenuItem>
                    </Select>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                      {suggestion.metadata?.admin_url && (
                        <Tooltip title="Edit venue">
                          <Link href={suggestion.metadata.admin_url} target="_blank">
                            <IconButton size="small">
                              <EditIcon />
                            </IconButton>
                          </Link>
                        </Tooltip>
                      )}
                      <Tooltip title="Delete suggestion">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDelete(suggestion.id, suggestion.venue_name)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default SuggestionsTable;